import * as SubscriptionService from '@root/lib/services/SubscriptionsService';
import { boldSubscriptionData, platformSubscriptionData } from '@root/lib/services/StorageService';
import {Mutex} from 'async-mutex';

/**
 * @typedef {Object} PlatformData
 * @property {string} customerId
 * @property {string} jwt
 *
 * @typedef {Object} BoldData
 * @property {string} jwt
 * @property {number} expiry
 * @property {Object} customer
 * @property {string} customer.cashierPublicId
 * @property {number} customer.id
 * @property {string} customer.platformId
 */

export class SubscriptionSession {

  /** @type {PlatformData} */
  #platformData;

  /** @type {BoldData} */
  #boldData;

  /** @type {{bold: Mutex, platform: Mutex}} */
  #mutexes;

  constructor() {
    this.#mutexes = {
      bold: new Mutex(),
      platform: new Mutex(),
    };

    this.#platformData = platformSubscriptionData.get(Shopify.customer?.id);
    this.#boldData = boldSubscriptionData.get(Shopify.customer?.id);
  }

  /**
   * @returns {Promise<BoldData>}
   */
  getBoldData = () => {
    return this.#mutexes.bold.runExclusive(async () => {
      const expiry = this.#boldData?.expiry;
      const now = Date.now();

      // Getting bold JWT if the token is expired or the JWT has not yet been fetch
      if (!this.#boldData || expiry < now) {
        const platformData = await this.getPlatformData();
        this.#boldData = await SubscriptionService.exchangePlatformJwt(platformData.customerId, platformData.jwt);
        boldSubscriptionData.set(Shopify.customer.id, this.#boldData);
      }

      return this.#boldData;
    });
  };

  /**
   * @returns {Promise<string>}
   */
  getBoldJwt = async () => {
    const boldData = await this.getBoldData();
    return boldData.jwt;
  };

  /**
   * @returns {Promise<number>}
   */
  getBoldCustomerId = async () => {
    const boldData = await this.getBoldData();
    return boldData.customer.id;
  };

  /**
   * @returns {Promise<string>}
   */
   getCashierPublicId = async () => {
    const boldData = await this.getBoldData();
    return boldData.customer.cashierPublicId;
  };

  /**
   * @returns {Promise<PlatformData>}
   */
  getPlatformData = () => {
    return this.#mutexes.platform.runExclusive(async () => {
      const expiry = this.#platformData?.expiry ?? 0;
      const now = Date.now();

      if (!this.#platformData || expiry < now) {
        const platformData = await SubscriptionService.getPlatformData();
        let jwtBody = null;

        try {
          let decodedJwt = atob(platformData.jwt.split('.')[0]);

          jwtBody = JSON.parse( decodedJwt );
        } catch ( error ) {
          console.error( 'Error processing session token.', error );
        }
        
        if ( jwtBody ) {
          this.#platformData = {...platformData, expiry: jwtBody.expiry};
          platformSubscriptionData.set(Shopify.customer.id, this.#platformData);
        }
      }

      return this.#platformData;
    });
  };

  /**
   * @returns {Promise<string>}
   */
  getPlatformJwt = async () => {
    const platformData = await this.getPlatformData();
    return platformData.jwt;
  };

  /**
   * @returns {Promise<number>}
   */
  getPlatformCustomerId = async () => {
    const platformData = await this.getPlatformData();
    return platformData.customerId;
  };
}

export default new SubscriptionSession();
